import React, { useRef, useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';

import { motion } from 'framer-motion';
import { Typography } from '@material-ui/core';

import Button from '@material-ui/core/Button';
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos';

import { Container, Row, Col, Image, Badge } from 'react-bootstrap';

import { AnimateChild, AnimateParent } from '../animation/StaggeredAnimation';

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundImage: 'linear-gradient(#ccd3eb, #fff8ef)',
  },
  title: {
    color: theme.palette.secondary.main,
  },
  description: {
    maxWidth: '950px',
    fontSize: 24,
    fontStyle: 'normal',
    fontWeight: 'normal',
    lineHeight: '31px',
    letterSpacing: '-0.04em',
    marginTop: '16px',
    [theme.breakpoints.down('sm')]: {
      fontSize: 18,
    },
  },
  descriptionSm: {
    maxWidth: '950px',
    fontSize: 20,
    fontStyle: 'normal',
    fontWeight: 'normal',
    lineHeight: '22px',
    letterSpacing: '-0.04em',
    marginTop: '8px',
    [theme.breakpoints.down('sm')]: {
      fontSize: 18,
    },
  },
  teamCardParent: {
    padding: '1.5em',
    borderRadius: '8px',
    color: '#edf1ff',
    background: theme.palette.secondary.main,
    transition: 'all 0.15s',
    '&:hover': {
      transform: 'translateY(-5px)',
      cursor: 'pointer',
    },
    '&:hover hoverLink': {
      transform: 'translateX(10px)',
    },
  },
  teamIcon: {
    width: '50%',
  },
  styledBtn: {
    fontSize: 18,
    lineHeight: '23px',
    borderRadius: '4px',
    textAlign: 'center',
    textTransform: 'none',
    letterSpacing: '-0.04rem',
    color: 'white !important',
  },
  descriptionCard: {
    padding: '1.5em',
    borderRadius: '8px',
    border: 'solid #cfcfcf 2px',
    background: 'white',
  },
}));

const TeamInformation = ({
  title,
  scrollToRef,
  children,
  application,
  isClosed,
}) => {
  const classes = useStyles();

  return (
    <div className={classes.descriptionCard} ref={scrollToRef}>
      <Row>
        <Col sm={12} md={8} className="mb-2">
          <Typography variant="h2" className={classes.title}>
            {title}
          </Typography>
        </Col>
        <Col className="text-md-right mb-3">
          <Button
            href={application[0].link}
            target="_blank"
            rel="noopener noreferrer"
            variant="contained"
            className={classes.styledBtn}
            color="primary"
            disabled={isClosed}>
            {application[0].text}
          </Button>
        </Col>
      </Row>
      <Row>
        <Col>{children}</Col>
      </Row>
    </div>
  );
};

const TeamCard = ({ title, imgURL, description, scrollFunction }) => {
  const classes = useStyles();

  return (
    <AnimateChild>
      <div className={classes.teamCardParent} onClick={scrollFunction}>
        <AnimateChild>
          <Row>
            <Col className="text-center d-none d-md-block">
              <img src={imgURL} className={classes.teamIcon} />
            </Col>
          </Row>
        </AnimateChild>
        <AnimateChild>
          <Row>
            <Col>
              <Typography variant="h3">{title}</Typography>
              {description}
            </Col>
          </Row>
          <Row className="mt-3">
            <Col>
              <Typography variant="body1" className={classes.hoverLink}>
                Learn more <ArrowForwardIosIcon fontSize="small" />
              </Typography>
            </Col>
          </Row>
        </AnimateChild>
      </div>
    </AnimateChild>
  );
};

const ApplicationPage = () => {
  const classes = useStyles();

  const designRef = useRef(null);
  const logiRef = useRef(null);
  const webRef = useRef(null);

  const designScroll = () =>
    designRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  const logiScroll = () =>
    logiRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  const webScroll = () =>
    webRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });

  // Used to disabled the button
  // const [currentTime, setCurrentTime] = useState(new Date());
  const [isClosed, setIsClosed] = useState(true);
  // const dueDate = new Date('2021-03-12T00:10:00-05:00');

  // // Keeps checking the time and calls a cleanup function after
  // useEffect(() => {
  //   const intervalID = setInterval(() => setCurrentTime(new Date()), 1000);

  //   return () => {
  //     clearInterval(intervalID);
  //   };
  // }, []);

  // // Checks to see whether its time to close submissions
  // useEffect(() => {
  //   if (currentTime - dueDate > 0) {
  //     setIsClosed(true);
  //   }
  // }, [currentTime]);

  return (
    <div className={classes.root}>
      <Container className="pt-4 pb-5">
        <Row>
          <Col>
            <Button
              color="primary"
              variant="contained"
              className={classes.styledBtn}
              href="/"
              style={{
                color: 'white',
              }}>
              Back Home
            </Button>
          </Col>
        </Row>
        <Row className="mt-5">
          <Col sm={12} md={7} className="mb-3">
            <Typography variant="h2" className={classes.title}>
              Join the Hack Western Team!
            </Typography>
            {isClosed ? (
              <Typography variant="h4" className={classes.title}>
                Applications are now closed - Thank you for applying! 🥳
              </Typography>
            ) : (
              <>
                <Badge variant="success">Applications Extended!</Badge>
                <Typography variant="h4" className={classes.title}>
                  Application Deadline: March 11th, 2021 at 11:59 PM EST
                </Typography>
              </>
            )}
            <Typography variant="body1" className={classes.description}>
              Thank you for your interest in applying to be a part of Hack
              Western. Entering our 8th year of operations, we're looking to
              grow our team to take on more ambitious projects as one of
              Canada's largest student-run hackathons.
            </Typography>
            <Typography variant="body1" className={classes.description}>
              Hack Western 8 will be held late November of 2021 as a{' '}
              <strong>virtual-first event</strong> with potential in-person
              components depending on local health authorities.
            </Typography>
            <Typography variant="body1" className={classes.description}>
              There are three types of roles where you can apply to, and you may
              apply to more than one.{' '}
              <strong>
                Even if you don't meet all requirements, still apply{' '}
              </strong>
              - many of us have learned the skills needed on the job! If
              accepted, your main focus will be within your designated roles
              with a lot of flexibility to try other areas as well.{' '}
              <strong>
                We aren't hiring you only for a specific team, but to be an
                organizer for Hack Western 8 as a whole!
              </strong>
            </Typography>
            <Typography
              variant="body1"
              className={classes.description}
              style={{ color: '#394F94' }}>
              <strong>
                Please note, only post-secondary students will be considered.
                Priority will be given to those attending Western University and
                affiliates.
              </strong>
            </Typography>
            <Typography variant="body1" className={classes.description}>
              Questions? Reach us at{' '}
              <a href="mailto:hello@hackwestern.com">hello@hackwestern.com</a>
            </Typography>
          </Col>
          <Col className="my-auto">
            <div className={classes.descriptionCard}>
              <Typography variant="h3" className={classes.title}>
                Talk to a human?
              </Typography>
              <Typography variant="body1" className={classes.descriptionSm}>
                Sign up for a chat with our current organizers!
              </Typography>
              <Button
                href="https://bit.ly/3e1LEgh"
                fullWidth
                target="_blank"
                rel="noopener noreferrer"
                variant="contained"
                className={`${classes.styledBtn} mt-3`}
                color="secondary">
                Book here
              </Button>
            </div>
          </Col>
        </Row>
        <Row className="mt-5">
          <Col>
            <Typography variant="h3" className={classes.title}>
              Open Roles
            </Typography>
          </Col>
        </Row>
        <AnimateParent>
          <Row className="mt-3">
            <Col sm={12} md={4} className="mb-3">
              <TeamCard
                title="Design"
                description="Create stunning videos, graphics, and branding that give Hack Western life"
                imgURL="/2020/static/media/pencil-holder.f971690d.png"
                scrollFunction={designScroll}
              />
            </Col>
            <Col sm={12} md={4} className="mb-3">
              <TeamCard
                title="Logistics & Sponsorship"
                description="Crafty generalists that support the critical infrastructure of the event"
                imgURL="/2020/static/media/backpack.695a2824.png"
                scrollFunction={logiScroll}
              />
            </Col>
            <Col sm={12} md={4} className="mb-3">
              <TeamCard
                title="Web"
                description="Ship tools and applications that power hackers around the world"
                imgURL="/2020/static/media/laptop-books.9d7c2fde.png"
                scrollFunction={webScroll}
              />
            </Col>
          </Row>
        </AnimateParent>

        <AnimateParent>
          <AnimateChild>
            <Row className="mt-5">
              <Col>
                <Typography variant="h2" className={classes.title}>
                  Apply Here
                </Typography>
                {isClosed ? (
                  <Typography variant="h4" className={classes.title}>
                    Applications are now closed - Thank you for applying! 🥳
                  </Typography>
                ) : (
                  <>
                    <Badge variant="success">Applications Extended!</Badge>
                    <Typography variant="h4" className={classes.title}>
                      Application Deadline: March 11th, 2021 at 11:59 PM EST
                    </Typography>
                  </>
                )}
              </Col>
            </Row>
          </AnimateChild>
        </AnimateParent>
        <AnimateParent>
          <Row className="mt-3">
            <Col>
              <TeamInformation
                title="Design"
                application={[
                  {
                    text: 'Apply Now',
                    link: 'https://forms.gle/4EiYupUgWAzBZTx4A',
                  },
                ]}
                scrollToRef={designRef}
                isClosed={isClosed}>
                <Row>
                  <Col sm={12} md={8}>
                    <Typography
                      variant="body1"
                      className={classes.descriptionSm}>
                      As a designer for the Hack Western team, you’ll be
                      ideating and designing for one of the largest hackathons
                      in Canada. Members will be involved in designing and
                      creating experiences for hackers and sponsors alike. This
                      includes the theme, marketing graphics, and web
                      applications.
                    </Typography>
                    <Typography
                      variant="body1"
                      className={classes.descriptionSm}>
                      Outside of role-specific tasks, organizers will also help
                      with the overall event and become ambassadors of the Hack
                      Western brand!
                    </Typography>
                    <Typography
                      variant="h4"
                      className={`${classes.title} mt-3`}>
                      We are looking for candidates who have
                    </Typography>
                    <ul className={classes.descriptionSm}>
                      <li>
                        Willingness to learn and be adaptable to constraints
                      </li>
                      <li>Strong attention to detail</li>
                      <li>
                        Curiosity and passion for design, eager to take
                        initiative to go above and beyond
                      </li>
                      <li>
                        Prior experience collaborating in a design team or
                        working in a team as a designer (whether for
                        illustration, graphics, or product)
                      </li>
                      <li>
                        Experience with design and prototyping tools i.e. Figma,
                        Adobe Creative Suite
                      </li>
                      <li>
                        <strong>BONUS: </strong>Experience in motion graphics
                        and After Effects, knowledge of HTML/CSS, experience
                        with designing for accessibility
                      </li>
                    </ul>
                  </Col>
                  <Col className="my-auto text-center d-none d-md-block">
                    <Image
                      src="/2020/static/media/pencil-holder.f971690d.png"
                      fluid
                    />
                  </Col>
                </Row>
              </TeamInformation>
            </Col>
          </Row>
        </AnimateParent>

        <Row className="mt-3">
          <Col>
            <TeamInformation
              title="Logistics & Sponsorship"
              application={[
                {
                  text: 'Apply Now',
                  link: 'https://forms.gle/yyv5dcTRGCSwaeDd7',
                },
              ]}
              scrollToRef={logiRef}
              isClosed={isClosed}>
              <Row>
                <Col sm={12} md={8}>
                  <Typography variant="body1" className={classes.descriptionSm}>
                    Team members will be involved in different aspects of event
                    planning and sponsorship for Hack Western. Projects include
                    planning opening and closing ceremonies, coordinating
                    workshops and activities, and building relationships with
                    sponsors.
                  </Typography>
                  <Typography variant="body1" className={classes.descriptionSm}>
                    Outside of role-specific tasks, organizers will also help
                    with the overall event and become ambassadors of the Hack
                    Western brand!
                  </Typography>
                  <Typography variant="h4" className={`${classes.title} mt-3`}>
                    We are looking for candidates who
                  </Typography>
                  <ul className={classes.descriptionSm}>
                    <li>
                      Have confidence in their professional oral and written
                      communication skills
                    </li>
                    <li>
                      Have strong time management and organization abilities to
                      coordinate multiple responsibilities at once
                    </li>
                    <li>
                      Have previous experience in event planning and/or
                      sponsorship roles
                    </li>
                    <li>
                      Can work closely with other members of the logistics team
                      as well as collaborate with team members from the design
                      and web team to execute projects
                    </li>
                    <li>Can take initiative and work independently</li>
                    <li>Are creative and able to think outside the box</li>
                  </ul>
                </Col>
                <Col className="my-auto text-center d-none d-md-block">
                  <Image src="/2020/static/media/backpack.695a2824.png" fluid />
                </Col>
              </Row>
            </TeamInformation>
          </Col>
        </Row>
        <Row className="mt-3">
          <Col>
            <TeamInformation
              title="Web"
              application={[
                {
                  text: 'Apply Now',
                  link: 'https://forms.gle/SFdzKxVRMvdFnCvTA',
                },
              ]}
              scrollToRef={webRef}
              isClosed={isClosed}>
              <Row>
                <Col sm={12} md={8}>
                  <Typography variant="body1" className={classes.descriptionSm}>
                    The web team is responsible for building any digital tool or
                    platform to run the event, from the initial landing page to
                    the applications that run the event, you'll be able to get
                    hands on experience building products to be used by
                    sponsors, hackers, and organizers alike.
                  </Typography>
                  <Typography variant="body1" className={classes.descriptionSm}>
                    Outside of role-specific tasks, organizers will also help
                    with the overall event and become ambassadors of the Hack
                    Western brand!
                  </Typography>
                  <Typography variant="h4" className={`${classes.title} mt-3`}>
                    We are looking for candidates who have
                  </Typography>
                  <ul className={classes.descriptionSm}>
                    <li>
                      Prior project or work experience with HTML, CSS, and
                      JavaScript. Experience in front-end frameworks such as
                      React or back-end technologies such as Node or Express is
                      a plus!
                    </li>
                    <li>Interest in building web applications</li>
                    <li>
                      A can-do mindset, willing to roll up their sleeves and
                      learn new skills
                    </li>
                    <li>
                      The ability to take initiative and work independently
                    </li>
                    <li>
                      <strong>BONUS: </strong>Experience with building Discord
                      Bots
                    </li>
                    <li>
                      <strong>BONUS: </strong>Interest in considering technical
                      tradeoffs and scalability for web applications
                    </li>
                    <li>
                      <strong>BONUS: </strong>Interest in writing technical
                      documentation and maintaining open-source projects
                    </li>
                  </ul>
                </Col>
                <Col className="my-auto text-center d-none d-md-block">
                  <Image src="/2020/static/media/laptop-books.9d7c2fde.png" fluid />
                </Col>
              </Row>
            </TeamInformation>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default ApplicationPage;
